import React from 'react';
import { RotateCcw, Target } from 'lucide-react';
import { useCricketFormat } from '@/hooks/useUnitPreferences';

interface AngleIndicatorProps {
  actual: number;
  optimal: number;
  min: number;
  max: number;
  unit: string;
  label: string;
  improvement: number;
}

export const AngleIndicator: React.FC<AngleIndicatorProps> = ({
  actual,
  optimal,
  min,
  max,
  unit: _unit,
  label,
  improvement
}) => {
  const { formatAngle, preferences: _preferences } = useCricketFormat();
  
  // Fallback function if formatting fails
  const safeFormatAngle = (val: number, includeUnit: boolean = true) => {
    try {
      return formatAngle(val, includeUnit);
    } catch (error) {
      console.warn('Unit formatting error:', error);
      return includeUnit ? `${val.toFixed(1)}°` : val.toFixed(1);
    }
  };

  const getAngleColor = () => {
    const difference = Math.abs(actual - optimal);
    if (difference <= 2) return '#10b981'; // green
    if (difference <= 5) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const angle = actual;
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  
  // Calculate positions for the angle indicator
  const angleRad = (angle * Math.PI) / 180;
  const optimalAngleRad = (optimal * Math.PI) / 180;
  
  const actualX = centerX + Math.cos(angleRad - Math.PI/2) * radius;
  const actualY = centerY + Math.sin(angleRad - Math.PI/2) * radius;
  
  const optimalX = centerX + Math.cos(optimalAngleRad - Math.PI/2) * radius;
  const optimalY = centerY + Math.sin(optimalAngleRad - Math.PI/2) * radius;

  return (
    <div className="space-y-4">
      {/* Angle Display */}
      <div className="text-center">
        <div className="text-3xl font-bold" style={{ color: getAngleColor() }}>
          {safeFormatAngle(actual)}
        </div>
        <p className="text-sm text-gray-600">{label}</p>
      </div>

      {/* Visual Angle Indicator */}
      <div className="relative mx-auto w-48 h-24 overflow-hidden">
        <svg width="200" height="100" viewBox="0 0 200 100" className="absolute inset-0">
          {/* Background arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="8"
          />
          
          {/* Optimal angle arc */}
          <path
            d={`M 20 100 A 80 80 0 0 1 ${optimalX} ${optimalY}`}
            fill="none"
            stroke="#22c55e"
            strokeWidth="4"
            opacity="0.6"
            strokeDasharray="5,5"
          />
          
          {/* Actual angle arc */}
          <path
            d={`M 20 100 A 80 80 0 0 1 ${actualX} ${actualY}`}
            fill="none"
            stroke={getAngleColor()}
            strokeWidth="6"
          />
          
          {/* Center point */}
          <circle cx="100" cy="100" r="3" fill="#374151" />
          
          {/* Actual angle line */}
          <line
            x1="100"
            y1="100"
            x2={actualX}
            y2={actualY}
            stroke={getAngleColor()}
            strokeWidth="3"
          />
          
          {/* Optimal angle line (dashed) */}
          <line
            x1="100"
            y1="100"
            x2={optimalX}
            y2={optimalY}
            stroke="#22c55e"
            strokeWidth="2"
            strokeDasharray="3,3"
            opacity="0.8"
          />
          
          {/* Actual angle marker */}
          <circle cx={actualX} cy={actualY} r="4" fill={getAngleColor()} />
          
          {/* Optimal angle marker */}
          <circle cx={optimalX} cy={optimalY} r="3" fill="#22c55e" opacity="0.8" />
        </svg>
        
        {/* Angle labels */}
        <div className="absolute bottom-0 left-0 text-xs text-gray-500">
          {safeFormatAngle(min)}
        </div>
        <div className="absolute bottom-0 right-0 text-xs text-gray-500">
          {safeFormatAngle(max)}
        </div>
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-xs text-gray-500">
          {safeFormatAngle((max + min) / 2)}
        </div>
      </div>

      {/* Comparison Data */}
      <div className="bg-gray-50 p-3 rounded-lg space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Actual:</span>
          <span className="font-medium">{safeFormatAngle(actual)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Optimal:</span>
          <span className="font-medium text-green-600">{safeFormatAngle(optimal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Adjustment:</span>
          <span className={`font-medium ${improvement >= 0 ? 'text-amber-600' : 'text-blue-600'}`}>
            {improvement >= 0 ? '+' : ''}{safeFormatAngle(Math.abs(improvement))}
          </span>
        </div>
      </div>

      {/* Accuracy Rating */}
      <div className="text-center">
        <div className="text-xs text-gray-500 mb-2">Accuracy Rating</div>
        <div className="flex justify-center items-center gap-1">
          {[...Array(5)].map((_, i) => {
            const difference = Math.abs(actual - optimal);
            const rating = Math.max(0, 5 - Math.floor(difference / 2));
            return (
              <Target
                key={i}
                className={`h-3 w-3 ${
                  i < rating ? 'text-current' : 'text-gray-300'
                }`}
                style={{ color: getAngleColor() }}
                fill="currentColor"
              />
            );
          })}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {Math.abs(actual - optimal) <= 2 ? 'Excellent' :
           Math.abs(actual - optimal) <= 5 ? 'Good' : 'Needs Work'}
        </div>
      </div>

      {/* Improvement Suggestion */}
      <div className="text-center text-xs text-gray-600 bg-blue-50 p-2 rounded">
        <RotateCcw className="h-3 w-3 inline mr-1" />
        {improvement > 2 ? `Increase angle by ${improvement.toFixed(1)}°` :
         improvement < -2 ? `Decrease angle by ${Math.abs(improvement).toFixed(1)}°` :
         'Angle is nearly optimal'}
      </div>
    </div>
  );
};